import type { WallOpening } from "../domain/framing/generateFramedWall";
import type { InteriorWall } from "../domain/interior/types";
import { clampPartitionSplit } from "../domain/partition/partitionSplit";
import { DEFAULT_FRONT_DOOR_ID } from "../pages/builder/defaultOpenings";
import {
	getTwoRoomLongEntryWall,
	isTwoRoomViewLayout,
	TWO_ROOM_PANORAMA_WALL,
	VIEW_LAYOUT_DOOR_CORNER_MM,
} from "./twoRoomConstants";
import type { BuildInput, TwoRoomConfig } from "./types";

export function getLongEntryDoorX(
	lengthMm: number,
	doorWidthMm: number,
	split: number,
	hasRelaxRoom: boolean,
	saunaPosition: TwoRoomConfig["saunaPosition"] = "left",
	viewLayout = false,
): number {
	if (viewLayout) {
		// The front corner, so the lavar can run the rest of the wall.
		return Math.round(lengthMm - VIEW_LAYOUT_DOOR_CORNER_MM - doorWidthMm);
	}
	if (!hasRelaxRoom) {
		return Math.round((lengthMm - doorWidthMm) / 2);
	}

	const saunaLengthMm = lengthMm * split;
	const relaxLengthMm = lengthMm - saunaLengthMm;
	if (saunaPosition === "right") {
		return Math.round((relaxLengthMm - doorWidthMm) / 2);
	}
	return Math.round(saunaLengthMm + (relaxLengthMm - doorWidthMm) / 2);
}

function createLongWallDoor(
	exteriorDoor: WallOpening,
	lengthMm: number,
	split: number,
	hasRelaxRoom: boolean,
	config: Pick<
		TwoRoomConfig,
		"glazingStyle" | "saunaPosition" | "hasRelaxRoom" | "entryWall"
	>,
): WallOpening {
	return {
		...exteriorDoor,
		id:
			exteriorDoor.id === DEFAULT_FRONT_DOOR_ID
				? "default-long-entry-door"
				: `${exteriorDoor.id}-long-entry`,
		// Panorama glazes the right wall, so the entry moves to the left wall.
		wall: getTwoRoomLongEntryWall(config),
		xMm: getLongEntryDoorX(
			lengthMm,
			exteriorDoor.widthMm,
			split,
			hasRelaxRoom,
			config.saunaPosition ?? "left",
			isTwoRoomViewLayout(config),
		),
	};
}

export function getTwoRoomFramingOpenings(
	openings: WallOpening[],
	lengthMm: number,
	split: number,
	config: TwoRoomConfig,
): WallOpening[] {
	if (config.entryWall !== "long") {
		return [];
	}

	const exteriorDoor = openings.find(
		(opening) => opening.type === "door" && opening.wall === "front",
	);

	return exteriorDoor
		? [
				createLongWallDoor(
					exteriorDoor,
					lengthMm,
					split,
					config.hasRelaxRoom,
					config,
				),
			]
		: [];
}

/**
 * A window on the panorama wall has nothing to be: the glass replaces the
 * whole wall, so the engine never frames or draws it. Kept in the draft it
 * still got a drag pill, a highlight and a dimension line floating inside
 * the glass, and "1 fönster" in the openings count. Drop it wherever the
 * draft takes on panorama glazing, and here for designs saved before that.
 */
export function withoutPanoramaWallWindows<T extends { wall: string; type: string }>(
	openings: readonly T[],
	config: { glazingStyle?: string } | null | undefined,
): T[] {
	if (config?.glazingStyle !== "panorama") return [...openings];
	return openings.filter(
		(opening) =>
			!(opening.wall === TWO_ROOM_PANORAMA_WALL && opening.type === "window"),
	);
}

export function resolveOpeningAdjustmentOpenings(
	input: BuildInput,
): BuildInput["openings"] {
	const openingsWithoutPanoramaWindows = withoutPanoramaWallWindows(
		input.openings,
		input,
	);

	if (input.entryWall !== "long") {
		return openingsWithoutPanoramaWindows;
	}

	const exteriorDoor = openingsWithoutPanoramaWindows.find(
		(opening) => opening.type === "door" && opening.wall === "front",
	);
	if (!exteriorDoor) {
		return openingsWithoutPanoramaWindows;
	}

	const lengthMm = Math.round(input.dimensions.length * 1000);
	const split = clampPartitionSplit(
		input.dimensions.length,
		input.partitionSplit ?? 0.5,
	);
	const entryConfig = {
		glazingStyle: input.glazingStyle ?? "standard",
		saunaPosition: input.saunaPosition ?? "left",
		hasRelaxRoom: input.hasRelaxRoom !== false,
		entryWall: input.entryWall,
	};
	const longWallDoor = createLongWallDoor(
		exteriorDoor,
		lengthMm,
		split,
		input.hasRelaxRoom !== false,
		entryConfig,
	);

	return openingsWithoutPanoramaWindows
		.filter(
			(opening) =>
				!(
					opening.type === "door" &&
					(opening.wall === "front" || opening.wall === longWallDoor.wall)
				),
		)
		.concat(longWallDoor);
}

/** Use the built openings, including relocated doors and the panorama wall.
 * Exclude their entire wall until a local bench-fixing plan exists. */
/** Floor-to-ceiling glass walls of the building, for dressing lav ends. */
export function resolveGlazedInteriorWalls(input: BuildInput): InteriorWall[] {
	return input.glazingStyle === "panorama" ? [TWO_ROOM_PANORAMA_WALL] : [];
}

export function resolveBenchSupportExcludedWalls(
	input: BuildInput,
): InteriorWall[] {
	const walls = new Set<string>(
		resolveOpeningAdjustmentOpenings(input).map((opening) => opening.wall),
	);
	if (input.glazingStyle === "panorama") walls.add(TWO_ROOM_PANORAMA_WALL);
	return [...walls].filter((wall): wall is InteriorWall =>
		["front", "back", "left", "right"].includes(wall),
	);
}
